import axios from "axios";
import { readClickIds } from "./attribution";
import type { StoredClickIds } from "./analyticsParams";

export async function createUser(params: Partial<Omit<UserCreateParams, "app" | "device_id_type">>) {
	const tvlUserAPIUrl = process.env.NEXT_PUBLIC_TVL_USER_API_URL;
	if (!tvlUserAPIUrl) throw new Error("TVL_USER_API_URL not set");

	const { data } = await axios.post<{ user_id: number; }>("/api/v1/users/add/", {
		app: "d1_web_car",
		device_id: params.device_id,
		device_id_type: 'browser',
		acquired_route: params.acquired_route,
		pseudo_id: params.pseudo_id,
		email: params.email,
		email_consent: params.email_consent
	} as UserCreateParams, {
		baseURL: tvlUserAPIUrl
	});

	return data.user_id;
}

/**
 * Starts a user session.
 *
 * Click ids default to the persisted attribution cookie, so a session started long after
 * the paid click still carries it. Ids passed explicitly by the caller win over the cookie.
 */
export async function startUserSession(params: Partial<UserSessionStartParams>, storedClickIds: StoredClickIds = readClickIds()) {
	const tvlUserAPIUrl = process.env.NEXT_PUBLIC_TVL_USER_API_URL;
	if (!tvlUserAPIUrl) throw new Error("TVL_USER_API_URL not set");

	await axios.post<{ user_id: string; }>("/api/v1/users/session/", {
		user_id: params.user_id,
		tag: params.tag,
		route: params.route ?? "organic",
		vertical: params.vertical ?? "car",
		country_code: params.country_code,
		gclid: params.gclid ?? storedClickIds.gclid,
		fbclid: params.fbclid ?? storedClickIds.fbclid,
		msclkid: params.msclkid ?? storedClickIds.msclkid,
		twclid: params.twclid ?? storedClickIds.twclid,
		ttclid: params.ttclid ?? storedClickIds.ttclid,
		attribution: {
			ad_id: storedClickIds.ad_id,
			campaign_id: storedClickIds.utm_id || storedClickIds.gad_campaignid,
			adgroup_id: storedClickIds.adset_id || storedClickIds.adgroup_id,
		}
	} as UserSessionStartParams, {
		baseURL: tvlUserAPIUrl
	})
		.catch(err => { console.error(err); });
}

/**
 * Saves a user's email along with the metadata needed to target and localise mailings.
 *
 * @param {UserEmailSaveParams} params - Payload for the save email API
 * @returns {Promise<boolean>} Success status of the save operation
 */
export async function saveUserEmail(params: UserEmailSaveParams): Promise<boolean> {
	const tvlUserAPIUrl = process.env.NEXT_PUBLIC_TVL_USER_API_URL;
	if (!tvlUserAPIUrl) {
		console.error("Environment variable NEXT_PUBLIC_TVL_USER_API_URL is not set");
		throw new Error("TVL_USER_API_URL not set");
	}

	try {
		// adapter+keepalive lets the POST survive same-tab navigation. Safari/iOS
		// cancel in-flight XHR on unload; native fetch with keepalive doesn't.
		await axios.post(
			"/api/v1/users/email/save/",
			params,
			{
				baseURL: tvlUserAPIUrl,
				adapter: "fetch",
				// Keep the browser's own User-Agent instead of axios'.
				headers: { "User-Agent": false },
				fetchOptions: { keepalive: true },
			}
		);

		return true;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			console.error("Failed to save user email:", {
				url: tvlUserAPIUrl + "/api/v1/users/email/save/",
				error: error.message,
				response: error.response?.data,
			});
			throw new Error(`Failed to save user email: ${error.message}`);
		}
		throw error;
	}
}
